import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    ArrowLeft, FileSpreadsheet, AlertCircle, Inbox, Upload, Trash2, Loader2, Play, Download, Info,
} from 'lucide-react';
import { selectUser } from '../store/authSlice';
import { commissionReportsApi } from '../api/commissionReportsApi';

const MONTH_LABELS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const monthLabel = (year, month) => `${MONTH_LABELS[month - 1]} ${year}`;

// File-kind values must match com.commissioner.service.CommissionRunService
// KIND_* constants exactly — the engine matches files by these strings and
// silently skips anything else. CRM and ADHOC are required for a run;
// LENDER_UPFRONT and LENDER_TRAIL are optional reference-only inputs.
const FILE_KINDS = [
    { value: 'CRM',            label: 'CRM output',     required: true  },
    { value: 'ADHOC',          label: 'Adhoc fees',     required: true  },
    { value: 'LENDER_UPFRONT', label: 'Lender upfront', required: false },
    { value: 'LENDER_TRAIL',   label: 'Lender trail',   required: false },
];

const KIND_LABEL = Object.fromEntries(FILE_KINDS.map((k) => [k.value, k.label]));

/**
 * Month detail. Shows input files (with upload + delete), a Run section
 * with a Run button (rates come from saved config), and the read-only
 * list of generated
 * reports.
 *
 * On first load: if no run exists for this month yet (404), we render an
 * empty state inviting the user to upload — the run is created server-side
 * when the first file arrives.
 */
export default function MonthDetailPage() {
    const { year, month } = useParams();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const orgUuid = user?.organisationUuid;

    const yearNum = Number(year);
    const monthNum = Number(month);

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Refresh whenever year/month/org changes.
    useEffect(() => {
        if (!orgUuid) return;
        let cancelled = false;
        loadDetail(orgUuid, yearNum, monthNum, cancelled, setDetail, setError, setLoading);
        return () => {
            cancelled = true;
        };
    }, [orgUuid, yearNum, monthNum]);

    // Local refresh helper used after uploads / deletes.
    const refresh = () => {
        if (!orgUuid) return;
        loadDetail(orgUuid, yearNum, monthNum, false, setDetail, setError, setLoading);
    };

    // After a run, the API returns the updated detail directly — feed it
    // straight into state so the new output file appears without a
    // second GET.
    const handleRunComplete = (updated) => setDetail(updated);

    const hasRequiredInputs = (detail?.inputFiles ?? []).some((f) => f.kind === 'CRM')
        && (detail?.inputFiles ?? []).some((f) => f.kind === 'ADHOC');

    return (
        <div className="px-8 py-8 max-w-5xl">
            <button
                onClick={() => navigate(`/commission-reports?year=${year}`)}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
            >
                <ArrowLeft size={14} />
                Back to {year}
            </button>

            <div className="mb-6">
                <h2 className="font-display text-2xl font-semibold text-slate-900">
                    {monthLabel(yearNum, monthNum)}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    {detail?.generatedAt
                        ? `Run completed ${formatDateTime(detail.generatedAt)}`
                        : detail
                            ? 'Not yet run'
                            : '—'}
                </p>
            </div>

            {loading && <div className="text-sm text-slate-400">Loading…</div>}

            {error && !loading && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {!loading && (
                <>
                    <InputFilesSection
                        orgUuid={orgUuid}
                        year={yearNum}
                        month={monthNum}
                        files={detail?.inputFiles ?? []}
                        onChange={refresh}
                    />

                    <div className="h-6" />

                    <RunSection
                        orgUuid={orgUuid}
                        year={yearNum}
                        month={monthNum}
                        canRun={hasRequiredInputs}
                        currentStatus={detail?.status}
                        failureReason={detail?.failureReason}
                        onRunComplete={handleRunComplete}
                    />

                    <div className="h-6" />

                    <OutputFilesSection
                        orgUuid={orgUuid}
                        year={yearNum}
                        month={monthNum}
                        files={detail?.outputFiles ?? []}
                        status={detail?.status}
                    />
                </>
            )}
        </div>
    );
}

// ── Loaders ────────────────────────────────────────────────────────────

function loadDetail(orgUuid, year, month, cancelled, setDetail, setError, setLoading) {
    setLoading(true);
    setError(null);
    commissionReportsApi
        .getMonth(orgUuid, year, month)
        .then((d) => {
            if (cancelled) return;
            setDetail(d);
        })
        .catch((err) => {
            if (cancelled) return;
            if (err.response?.status === 404) {
                // Empty state — no run record yet. Treat as if the month exists with empty file lists.
                setDetail({ year, month, status: 'NO_DATA', inputFiles: [], outputFiles: [] });
            } else {
                setError(err.response?.data?.message ?? err.message);
            }
        })
        .finally(() => !cancelled && setLoading(false));
}

// ── Input files (uploadable) ───────────────────────────────────────────

function InputFilesSection({ orgUuid, year, month, files, onChange }) {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);
    // Which kind to tag the next upload with — pick from the dropdown
    // before clicking Upload.
    const [kind, setKind] = useState('CRM');

    const handlePick = () => {
        setUploadError(null);
        fileInputRef.current?.click();
    };

    const handleChange = async (e) => {
        const file = e.target.files?.[0];
        // Reset the input so the same file can be re-selected after a failure.
        e.target.value = '';
        if (!file) return;

        setUploading(true);
        try {
            await commissionReportsApi.uploadInputFile(orgUuid, year, month, file, kind);
            onChange();
        } catch (err) {
            setUploadError(err.response?.data?.message ?? err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (fileId) => {
        if (!confirm('Delete this file? This cannot be undone.')) return;
        try {
            await commissionReportsApi.deleteInputFile(orgUuid, year, month, fileId);
            onChange();
        } catch (err) {
            setUploadError(err.response?.data?.message ?? err.message);
        }
    };

    return (
        <section>
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h3 className="font-display text-base font-semibold text-slate-900">
                        Input files
                    </h3>
                    <p className="text-xs text-slate-500">
                        CRM output and Adhoc fees are required. Lender invoices are optional.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        disabled={uploading}
                        className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
                    >
                        {FILE_KINDS.map((k) => (
                            <option key={k.value} value={k.value}>
                                {k.label}{k.required ? '' : ' (optional)'}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={handlePick}
                        disabled={uploading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 rounded-md"
                    >
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleChange}
                        className="hidden"
                    />
                </div>
            </div>

            {uploadError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{uploadError}</span>
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {files.length === 0 ? (
                    <div className="px-4 py-8 flex flex-col items-center gap-2 text-center">
                        <Inbox size={20} className="text-slate-300" />
                        <p className="text-sm text-slate-400">No input files uploaded yet.</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {files.map((f) => (
                            <FileRow
                                key={f.id}
                                file={f}
                                onDownload={() => downloadFileAndSave(
                                    orgUuid, year, month, f.id, f.name)}
                                onDelete={() => handleDelete(f.id)}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

// ── Run (rates + execute button) ───────────────────────────────────────

/**
 * Run section: an info banner explaining that rates come from the saved
 * rate config, plus a Run button. Disabled until the user has uploaded
 * both required inputs (CRM + ADHOC). After a successful run, the parent
 * gets the new run state and the output file appears in the section below.
 *
 * Commission rates are not entered per-run here. They're resolved from the
 * organisation's saved rate config (Configuration → Rates). The run sends
 * no rates; the backend looks them up. If no config is saved, the backend
 * returns a 400 and the message is shown inline.
 */
function RunSection({ orgUuid, year, month, canRun, currentStatus, failureReason, onRunComplete }) {
    const [running, setRunning] = useState(false);
    const [runError, setRunError] = useState(null);

    const handleRun = async () => {
        setRunError(null);
        setRunning(true);
        try {
            // No rates passed — the backend uses the org's saved rate config.
            const updated = await commissionReportsApi.executeRun(
                orgUuid, year, month);
            onRunComplete(updated);
            // executeRun can succeed at the HTTP layer but still come back
            // with status=FAILED (e.g. missing inputs detected server-side
            // or a malformed xlsx). Surface that as an inline error.
            if (updated.status === 'FAILED') {
                setRunError(updated.failureReason ?? 'Run failed.');
            }
        } catch (err) {
            setRunError(err.response?.data?.message ?? err.message);
        } finally {
            setRunning(false);
        }
    };

    return (
        <section>
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h3 className="font-display text-base font-semibold text-slate-900">
                        Run
                    </h3>
                    <p className="text-xs text-slate-500">
                        Calculates commissions using your saved base rates
                        and produces an output workbook.
                    </p>
                </div>
                <button
                    onClick={handleRun}
                    disabled={!canRun || running}
                    title={
                        !canRun
                            ? 'Upload CRM output and Adhoc fees first'
                            : 'Run the commission calculation'
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                >
                    {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                    {running ? 'Running…' : currentStatus === 'COMPLETED' ? 'Re-run' : 'Run'}
                </button>
            </div>

            {!canRun && (
                <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600">
                    Upload the CRM output and Adhoc fees workbooks before running.
                </div>
            )}

            {/* Show the most recent failure reason from a previous run so
                the user can see why their last attempt didn't work even
                after navigating away and back. */}
            {!runError && currentStatus === 'FAILED' && failureReason && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-medium">Last run failed</p>
                        <p className="mt-0.5">{failureReason}</p>
                    </div>
                </div>
            )}

            {runError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{runError}</span>
                </div>
            )}

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start gap-2 text-sm text-blue-800">
                <Info size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                    <p className="font-medium">Rates come from your saved configuration</p>
                    <p className="mt-0.5 text-blue-700">
                        This run uses the base commission rates saved under{' '}
                        <a href="/configuration" className="underline hover:text-blue-900">
                            Configuration
                        </a>
                        . Update them there to change what this run pays.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ── Download helper ────────────────────────────────────────────────────

/**
 * Hit the backend's download endpoint, then trigger a browser save using
 * the filename the server provides in {@code Content-Disposition}. Bytes
 * flow API → axios blob → object URL → anchor click → user's downloads.
 *
 * <p>The object URL is revoked on a short timeout to avoid leaking memory
 * across many downloads (Chrome tends to be patient about freeing them
 * but Safari is stricter).</p>
 *
 * @param fallbackName used if the response Content-Disposition is
 *   missing or unparseable — typically the {@code file.name} we already
 *   have in state from the run detail.
 */
async function downloadFileAndSave(orgUuid, year, month, fileId, fallbackName) {
    const res = await commissionReportsApi.downloadFile(
        orgUuid, year, month, fileId);

    const filename = parseContentDispositionFilename(
        res.headers?.['content-disposition']) ?? fallbackName ?? 'download';

    const blob = res.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

/**
 * Pull the filename out of a {@code Content-Disposition} header value.
 * Prefers the RFC 5987 {@code filename*=UTF-8''...} variant (handles
 * non-ASCII names correctly via percent-decoding), falls back to the
 * legacy quoted {@code filename="..."}. Returns null when neither parses.
 */
function parseContentDispositionFilename(header) {
    if (!header) return null;
    // RFC 5987 form: filename*=UTF-8''percent-encoded
    const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
    if (star) {
        try { return decodeURIComponent(star[1]); }
        catch { /* fall through to legacy form */ }
    }
    // Legacy quoted form: filename="X.xlsx" or filename=X.xlsx
    const legacy = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    return legacy ? legacy[1] : null;
}

// ── Output files (read-only) ───────────────────────────────────────────

function OutputFilesSection({ orgUuid, year, month, files, status }) {
    return (
        <section>
            <div className="mb-3">
                <h3 className="font-display text-base font-semibold text-slate-900">
                    Generated reports
                </h3>
                <p className="text-xs text-slate-500">
                    Commission reports produced by the run.
                </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {files.length === 0 ? (
                    <div className="px-4 py-8 flex flex-col items-center gap-2 text-center">
                        <Inbox size={20} className="text-slate-300" />
                        <p className="text-sm text-slate-400">
                            {status === 'IN_PROGRESS'
                                ? 'Reports will appear here when the run completes.'
                                : 'No reports generated yet.'}
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {files.map((f) => (
                            <FileRow
                                key={f.id}
                                file={f}
                                onDownload={() => downloadFileAndSave(
                                    orgUuid, year, month, f.id, f.name)}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

function FileRow({ file, onDownload, onDelete }) {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        if (!onDownload || downloading) return;
        setDownloading(true);
        try {
            await onDownload();
        } catch (err) {
            // No inline error UI on the row — too cramped. Browser shows
            // a failed network request, which is sufficient signal for
            // the user to retry. Log so support can see it.
            // eslint-disable-next-line no-console
            console.error('File download failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <li className="px-4 py-3 flex items-center gap-3">
            <FileSpreadsheet size={18} className="text-emerald-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                    {formatBytes(file.sizeBytes)} · {formatDateTime(file.timestamp)}
                </p>
            </div>
            <span className="text-xs text-slate-400 font-mono whitespace-nowrap">
                {KIND_LABEL[file.kind] ?? file.kind}
            </span>
            {onDownload && (
                <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="text-slate-400 hover:text-brand-600 p-1 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Download file"
                >
                    {downloading
                        ? <Loader2 size={16} className="animate-spin" />
                        : <Download size={16} />}
                </button>
            )}
            {onDelete && (
                <button
                    onClick={onDelete}
                    className="text-slate-400 hover:text-red-600 p-1 rounded"
                    title="Delete file"
                >
                    <Trash2 size={16} />
                </button>
            )}
        </li>
    );
}

function formatBytes(bytes) {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}